import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { warning } from '../../../../../components/Alert/Alert';
import Input from '../../../../../components/Input/Input';
import { SelectCreate } from '../../../../../components/SelectCreate/SelectCreate';
import { useAppDispatch, useAppSelector } from '../../../../../reduxService/hooks';
import { EConversionSIToImperial, conversionLabel, convertTo } from '../../../../conversion';
import { selectProjectInfo } from '../../../../store/projectInfo/projectInfoSlice';
import { selectProject } from '../../../selector/conversion';
import { setWingsGeometry } from '../../../store/project/projectSlice';
import { cleanResults } from '../../../store/project/resultsSlice';
import { IWeirGeometryVerticalDropForm } from '../WeirGeometry/IWeirGeometry';
import { IWingsGeometryProps, IWingsGeometryVerticalDropForm } from './IWingsGeometry';
import { UnitOfMeasure } from '../../../../enum/project';

const heightOptions = [
    { value: 0.5, label: '0.5' },
    { value: 1, label: '1' },
    { value: 1.5, label: '1.5' },
    { value: 2, label: '2' },
    { value: 2.5, label: '2.5' },
    { value: 3, label: '3' },
];
const heightOptionsImp = [
    { value: 1.5, label: '1.5' },
    { value: 3, label: '3' },
    { value: 4.5, label: '4.5' },
    { value: 6, label: '6' },
    { value: 7, label: '7' },
    { value: 9, label: '9' },
];
const WingsGeometryVerticalDrop = (props: IWingsGeometryProps) => {
    let timer = useRef<NodeJS.Timeout | null>(null);
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const project = useAppSelector(selectProject);
    const projectInfo = useAppSelector(selectProjectInfo);

    let firstGabion = (project.data!.weirGeometry! as IWeirGeometryVerticalDropForm).layer[1];

    const defaultValue: IWingsGeometryVerticalDropForm = {
        offset: (project.data!.wingsGeometry as IWingsGeometryVerticalDropForm).offset,
        width: (project.data!.wingsGeometry as IWingsGeometryVerticalDropForm).width,
        height: (project.data!.wingsGeometry as IWingsGeometryVerticalDropForm).height,
        slopeAngle: (project.data!.wingsGeometry as IWingsGeometryVerticalDropForm).slopeAngle,
    };

    const [form, setForm] = useState<IWingsGeometryVerticalDropForm>(defaultValue);
    let isImperial = projectInfo?.data?.unitOfMeasure == UnitOfMeasure.Imperial;

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let name = e.target.name;
        let value = parseFloat(e.target.value);

        if (value < 0) {
            value = 0;
            warning(t('warning_not_be_lower', { value: 0 }));
        } else if (name == 'slopeAngle' && value > 90) {
            value = 90;
            warning(t('warning_not_be_higher', { value: 90 }));
        } else if (name == 'offset') {
            let wingsGabion = (project.data?.weirGeometry as IWeirGeometryVerticalDropForm).layer.find(y => y.layerNumber == 1);
            let nextGabion = (project.data?.weirGeometry as IWeirGeometryVerticalDropForm).layer.find(y => y.layerNumber == 2);
            if (value > nextGabion?.layerWidth! - wingsGabion!.layerWidth) {
                value = nextGabion?.layerWidth! - wingsGabion?.layerWidth!;
                warning(t('warning_not_be_higher', { value: value }));
            }
            if (value > 15) {
                value = 15;
                warning(t('warning_not_be_higher', { value: value }));
            }
        } else if (name == 'width' && value > project.data!.geometry!.channelWidth) {
            value = project.data!.geometry!.channelWidth;
            warning(t('warning_not_be_higher', { value: value }));
        }

        setForm({
            ...form!,
            [name]: value,
        });
    };

    useEffect(() => {
        if (project.data!.geometry!.channelWidth < form.width!) {
            setForm({
                ...form!,
                width: project.data!.geometry!.channelWidth,
            });
        }
    }, [project.data!.geometry!.channelWidth]);

    useEffect(() => {
        if (timer.current) {
            clearTimeout(timer.current);
        }
        timer.current = setTimeout(() => {
            dispatch(cleanResults());
            dispatch(setWingsGeometry(form as IWingsGeometryVerticalDropForm));
        }, 1000);
    }, [form]);

    const handleOnChangeSelect = (value: any) => {
        if (value === 3 && firstGabion.layerWidth < 3) {
            value = 2.5;
        }
        setForm({
            ...form!,
            height: value,
        });
    };

    return (
        <div className='d-flex flex-column gap-2'>
            <Input
                type='number'
                title={t('input_crest_width', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}
                value={form.width}
                name='width'
                className='d-flex justify-content-between align-items-center'
                onChange={handleInputChange}
            />
            <Input
                type='number'
                title={t('input_layerOffset', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}
                name='offset'
                value={form.offset}
                className='d-flex justify-content-between align-items-center'
                onChange={handleInputChange}
            />
            <div className='d-flex justify-content-between align-items-center'>
                <label htmlFor='height'>{t('input_height', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}</label>
                <SelectCreate value={form.height} name='height' options={isImperial ? heightOptionsImp : heightOptions} parentOnChange={(newValue: any) => handleOnChangeSelect(newValue)} />
            </div>
            <Input type='number' title={t('input_slopeAngle')} name='slopeAngle' value={form.slopeAngle} className='d-flex justify-content-between align-items-center' onChange={handleInputChange} />
        </div>
    );
};

export default WingsGeometryVerticalDrop;
